import {
  Alert,
  Box,
  Button,
  Code,
  Divider,
  Drawer,
  Group,
  Select,
  Stack,
  Switch,
  Text,
  Textarea,
  TextInput,
  Title,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { HelpIcon, helpText, nextStepToast } from "@dehub/theme";
import { WarningCircleIcon } from "@phosphor-icons/react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { NotificationTemplateApi } from "../../apis";
import type {
  INotificationTemplate,
  IRenderTestResult,
} from "../../apis/notification-template";
import { PermissionButton } from "../ui";
import { message } from "../../utils/message";

type TFn = (k: string, o?: Record<string, unknown>) => string;

// O169 — the channel enum is the BE NOTIFICATION_CHANNEL (SMS / EMAIL / PUSH).
const channelOptions = (t: TFn) => [
  { value: "SMS",   label: t("templateEditor.channel.SMS") },
  { value: "EMAIL", label: t("templateEditor.channel.EMAIL") },
  { value: "PUSH",  label: t("templateEditor.channel.PUSH") },
];

const HANDLEBARS_VARS = [
  "{{returnNumber}}",
  "{{businessName}}",
  "{{amount}}",
  "{{currency}}",
  "{{reason}}",
  "{{link}}",
];

type FormValues = {
  templateKey: string;
  channel: string;
  subject: string;
  body: string;
  isActive: boolean;
};

function toFormValues(t?: INotificationTemplate | null): FormValues {
  return {
    templateKey: t?.templateKey ?? "",
    channel:     t?.channel     ?? "SMS",
    subject:     t?.subject     ?? "",
    body:        t?.body        ?? "",
    isActive:    t?.isActive    ?? true,
  };
}

interface Props {
  opened: boolean;
  template?: INotificationTemplate | null;
  onClose: () => void;
  onSaved: () => void;
}

/** @dehub/help key namespace of the notification-template screen (spec 12 §0). */
const HELP = "order.returns-settings-notification-templates";

export function TemplateEditorDrawer({ opened, template, onClose, onSaved }: Props) {
  const { t } = useTranslation("returns");
  const isEdit = !!template;
  const [submitting, setSubmitting] = useState(false);

  // Preview state
  const [variablesJson, setVariablesJson] = useState("{}");
  const [previewing, setPreviewing] = useState(false);
  const [previewResult, setPreviewResult] = useState<IRenderTestResult | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);

  const form = useForm<FormValues>({
    initialValues: toFormValues(template),
    validate: {
      // @dehub/help validation rows (R14), the locale message stays the fallback.
      templateKey: (v) => (!v.trim() ? helpText(`${HELP}.validation.template-key`) ?? t("templateEditor.keyRequired") : null),
      body:        (v) => (!v.trim() ? helpText(`${HELP}.validation.body`) ?? t("templateEditor.bodyRequired") : null),
      channel:     (v) => (!v ? helpText(`${HELP}.validation.channel`) ?? t("templateEditor.channelRequired") : null),
    },
  });

  // Reset form when template changes (drawer opened with different template)
  useEffect(() => {
    if (opened) {
      form.setValues(toFormValues(template));
      setPreviewResult(null);
      setPreviewError(null);
      setVariablesJson("{}");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, template]);

  const handleSubmit = async (values: FormValues) => {
    setSubmitting(true);
    try {
      const payload: Partial<INotificationTemplate> = {
        templateKey: values.templateKey.trim(),
        channel:     values.channel,
        subject:     values.channel === "EMAIL" ? (values.subject.trim() || null) : null,
        body:        values.body.trim(),
        isActive:    values.isActive,
      };

      if (isEdit && template) {
        await NotificationTemplateApi.update(template.id, payload);
      } else {
        await NotificationTemplateApi.create(payload);
      }

      nextStepToast(`${HELP}.toast.saved`, { fallback: { title: isEdit ? t("templateForm.updated") : t("templateForm.created") } });

      onSaved();
      onClose();
    } catch (err: any) {
      message.error(err?.message ?? t("templateForm.saveFailed"));
    } finally {
      setSubmitting(false);
    }
  };

  const handlePreview = async () => {
    setPreviewError(null);
    setPreviewResult(null);

    let parsedVariables: Record<string, unknown>;
    try {
      parsedVariables = JSON.parse(variablesJson);
    } catch {
      setPreviewError(t("templateEditor.jsonInvalid"));
      return;
    }

    setPreviewing(true);
    try {
      const result = await NotificationTemplateApi.renderTest({
        body:      form.values.body,
        subject:   form.values.channel === "EMAIL" ? form.values.subject || null : null,
        variables: parsedVariables,
      });
      setPreviewResult(result);
    } catch (err: any) {
      setPreviewError(err?.message ?? t("templateEditor.previewError"));
    } finally {
      setPreviewing(false);
    }
  };

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      position="right"
      size={720}
      title={
        <Title order={4}>
          {isEdit ? t("templateForm.editTitle") : t("templateEditor.newTitle")}
        </Title>
      }
    >
      <Box component="form" onSubmit={form.onSubmit(handleSubmit)}>
        <Stack gap="md">
          {/* Core fields */}
          <TextInput
            label={<>{t("notifTemplatesPage.col.templateKey")}<HelpIcon helpKey={`${HELP}.field.col-template-key`} /></>}
            placeholder="RETURN_PROPOSED"
            description={t("templateEditor.keyDesc")}
            {...form.getInputProps("templateKey")}
          />

          <Select
            label={t("notifTemplatesPage.channel")}
            data={channelOptions(t)}
            {...form.getInputProps("channel")}
          />

          {form.values.channel === "EMAIL" && (
            <TextInput
              label={t("templateEditor.subjectLabel")}
              placeholder={t("templateEditor.subjectPlaceholder")}
              {...form.getInputProps("subject")}
            />
          )}

          <Textarea
            label={<>{t("templateEditor.bodyLabel")}<HelpIcon helpKey={`${HELP}.field.template-body`} /></>}
            placeholder={t("templateEditor.bodyPlaceholder")}
            autosize
            minRows={8}
            {...form.getInputProps("body")}
          />

          <Group justify="space-between" wrap="nowrap">
            <Text size="sm" fw={600}>{t("notifTemplatesPage.active")}</Text>
            <Switch
              aria-label={t("notifTemplatesPage.active")}
              checked={form.values.isActive}
              onChange={(e) => form.setFieldValue("isActive", e.currentTarget.checked)}
            />
          </Group>

          {/* Handlebars variable hints */}
          <Box>
            <Text size="sm" fw={500} c="var(--nw-text-2)" mb={4}>
              {t("templateEditor.availableVars")}
            </Text>
            <Group gap={6} wrap="wrap">
              {HANDLEBARS_VARS.map((v) => (
                <Code key={v}>{v}</Code>
              ))}
            </Group>
          </Box>

          <Divider label={t("templateEditor.previewSection")} labelPosition="left" />

          {/* Variables JSON editor */}
          <Textarea
            label={<>{t("templateEditor.testVars")}<HelpIcon helpKey={`${HELP}.field.test-vars`} /></>}
            description={t("templateEditor.testVarsDesc")}
            placeholder={t("templateEditor.testVarsPlaceholder")}
            value={variablesJson}
            onChange={(e) => setVariablesJson(e.currentTarget.value)}
            autosize
            minRows={4}
            styles={{ input: { fontFamily: "var(--mantine-font-family-monospace)" } }}
          />

          <Button
            variant="default"
            w="fit-content"
            onClick={handlePreview}
            loading={previewing}
            type="button"
          >
            {t("templateEditor.showPreview")}
          </Button>

          {/* Parse error */}
          {previewError && (
            <Alert
              icon={<WarningCircleIcon size={16} />}
              color="gray"
              title={t("toast.error")}
            >
              {previewError}
            </Alert>
          )}

          {/* API errors[] */}
          {previewResult?.errors && previewResult.errors.length > 0 && (
            <Alert
              icon={<WarningCircleIcon size={16} />}
              color="gray"
              title={t("templateEditor.templateError")}
            >
              <Stack gap={4}>
                {previewResult.errors.map((err, i) => (
                  <Text key={i} size="sm">
                    {err}
                  </Text>
                ))}
              </Stack>
            </Alert>
          )}

          {/* Rendered preview */}
          {previewResult && !previewResult.errors?.length && (
            <Stack gap="xs">
              {previewResult.subject && (
                <Box>
                  <Text size="sm" fw={500} c="var(--nw-text-2)" mb={4}>
                    {t("templateEditor.subjectPreview")}
                  </Text>
                  <Code block>{previewResult.subject}</Code>
                </Box>
              )}
              <Box>
                <Text size="sm" fw={500} c="var(--nw-text-2)" mb={4}>
                  {t("templateEditor.bodyPreview")}
                </Text>
                <Code block>{previewResult.body}</Code>
              </Box>
            </Stack>
          )}

          <Divider />

          <Group justify="flex-end">
            <Button variant="default" onClick={onClose} type="button">
              {t("templateForm.close")}
            </Button>
            <PermissionButton permission="BIZ_GOV_MNG" type="submit" loading={submitting}>
              {isEdit ? t("resolutionPanel.save") : t("templateForm.create")}
            </PermissionButton>
          </Group>
        </Stack>
      </Box>
    </Drawer>
  );
}
